<template>
    <div>
        <div id="exportable-content">
            <Hero title="Designed to safeguard your customers' data" :highlight-words="['safeguard']"
                subtitle="Unified safeguards customer data by design: no data storage at rest, ever. We pair this with SOC 2 Type II, GDPR, CCPA, HIPAA, and PIPEDA certifications, plus segregated multi-region hosting." />

            <div
                class="w-full text-sm font-medium text-center text-gray-400 flex flex-wrap justify-center gap-12 items-center mb-20">
                <a href="https://app.mycroft.io/trust/unified-to" target="_blank" class="flex items-center gap-2">
                    <img src="/images/soc2-logo.png" alt="SOC 2" class="h-32 object-contain" />
                </a>
                <a href="https://app.mycroft.io/trust/unified-to" target="_blank" class="flex items-center gap-2">
                    <img src="/images/gdpr-logo.png" alt="GDPR" class="h-32 object-contain" />
                </a>
                <a href="https://app.mycroft.io/trust/unified-to" target="_blank" class="flex items-center gap-2">
                    <img src="/images/ccpa-logo.png" alt="CCPA" class="h-32 object-contain" />
                </a>
                <a href="https://app.mycroft.io/trust/unified-to" target="_blank" class="flex items-center gap-2">
                    <img src="/images/hipaa-logo.png" alt="HIPAA" class="h-32 object-contain" />
                </a>
                <a href="https://app.mycroft.io/trust/unified-to" target="_blank" class="flex items-center gap-2">
                    <img src="/images/pipeda-logo.png" alt="PIPEDA" class="h-28 object-contain" />
                </a>
            </div>



            <Benefits bottom_image :benefits="[
                {
                    title: 'Data handling',
                    body: `Unified.to uses a passthrough, no-storage architecture: customer data never resides on our servers—it's fetched live from the source, transformed and then delivered to your system in real time. To minimize exposure, Unified never stores detailed logs.`,
                },
                {
                    title: 'Compliance frameworks',
                    body: `Unified is SOC 2 Type II certified, and GDPR, CCPA/CPRA, PIPEDA and HIPAA compliant. Our no-storage architecture removes PII persistence risk, simplifying security reviews and reducing audit scope.<br/><br/>Request any of our security report from our <a class='underline' href='https://app.mycroft.io/trust/unified-to' target='_blank'>Trust Center</a>.`,
                },
                {
                    title: 'Encryption',
                    body: `All data in transit uses TLS 1.2+. Minimal operational metadata is encrypted at rest with AES-256. Customer OAuth2 credentials and end-customer API tokens can be optionally stored in your own AWS Secrets Manager, Google, Azure, or HashiCorp Vault.`,
                },
                {
                    title: 'SSO & SAML',
                    body: `Control access to the Unified's dashboard with SAML-based Single Sign-On. Enforce your IdP's policies (MFA, conditional access) and pair with role-based permissions and IP allowlisting.`,
                },
                {
                    title: 'Application security',
                    body: `Unified undergoes annual third-party penetration testing. Regular automated vulnerability scans are performed on containers and infrastructure. Every dev build is scanned with SAST. Runtime is covered by DAST.`,
                },
                {
                    title: 'IP address restrictions',
                    body: `Ensure only your servers can interact with Unified.to by restricting API access to specified IP addresses.`,
                },
            ]" />

            <Benefits center title="Data regions" subtitle="Completely segregated data regions for maximum security."
                :benefits="[
                    {
                        title: 'United States',
                        icon: 'flag:us-4x3',
                    },
                    {
                        title: 'Europe',
                        icon: 'flag:eu-4x3'
                    },
                    {
                        title: 'Australia',
                        icon: 'flag:au-4x3'
                    },
                ]" />

            <div class="w-full text-sm font-medium text-center text-gray-400">
                <SubTitle title="Additional resources" class="mb-4" />
                <ul class="flex flex-wrap justify-center gap-4 -mb-px">
                    <li class="lg:mr-2 border-b border-[var(--light-gray)]" v-for="link in links">
                        <a :href="link.url" target="_blank">
                            <button
                                class="inline-block lg:px-4 py-4 px-2 border rounded-lg hover:text-[var(--medium-gray)] hover:border-[var(--primary-blue)] hover:bg-[var(--hover-gray)]">
                                {{ link.label }}
                            </button>
                        </a>
                    </li>
                </ul>
            </div>


            <section class="mt-3 sm:mt-12 px-3 sm:px-28">
                <h2 class="plan_name font-bold border-b-2 mb-4 mt-0 pb-2 sm:px-8 px-2">Security FAQ</h2>

                <Accordion v-model="accordion_method" :labels="faqs.map((f) => f.title)" class="mt-8">
                    <template v-for="(faq, index) of faqs" v-slot:[getVSlot(index)]>
                        <a class="anchor" :name="faq.id" :id="faq.id" />
                        <span v-html="faq.answer.replace(/\n/g, '<br>')"></span>
                    </template>
                </Accordion>
            </section>

            <p class="mt-12 text-center text-xs text-gray-500">Last security review: February 2025</p>
        </div>

    </div>
</template>

<script lang="ts">
export default {
    setup() {
        const faqs = [
            {
                id: 'single-tenant',
                title: 'Does Unified offer a one-prem or single-tenant cloud hosting?',
                answer: 'Yes. Contact us to discuss our Enterprise plan which has options for on-prem or single-tenant cloud hosting.',
            },
            {
                id: 'linked-account-removal',
                title: 'What happens to data when a linked account is removed?',
                answer: "All associated configuration data is deleted immediately. Because Unified never stores end-customer data, there is nothing else to persist or erase.",
            },
            {
                id: 'logs-storage',
                title: 'Where are logs stored and who controls them?',
                answer: "End-customer data is never written to Unified's logs. Operational metadata is minimized and redacted. There is an option to stream detailed logs directly to your own Datadog, Grafana, or ClickHouse.",
            },
            {
                id: 'mcp-sensitive-data',
                title: 'How does Unified MCP handle sensitive data in LLM API workflows?',
                answer: 'MCP makes only scoped, authorized end-customer data available to your LLM tools. There is an option to remove PII sensitive data. There are additional restrictive configuration options to limit access.',
            },
            {
                id: 'data-residency',
                title: 'Where is Unified hosted, and how is data residency handled?',
                answer: 'Unified runs on AWS, leveraging enterprise-grade physical and network security. Customers can select any AWS region for credential and secrets storage, with multi-tenant options in the US, EU, and APAC.',
            },
            {
                id: 'sso-saml',
                title: 'Do you support SSO / SAML?',
                answer: 'Yes. Unified supports SAML 2.0 and OIDC SSO. Connect your IdP (e.g., Okta, Microsoft Entra ID/Azure AD, Google Workspace, Ping, OneLogin) to enforce org policies like MFA and conditional access.',
            },
            {
                id: 'soc2',
                title: 'Is Unified.to SOC 2 compliant?',
                answer: "Unified.to has successfully completed the SOC 2 Type 2 audit and is now fully compliant to AICPA's Trust Services Criteria 2017 (SOC 2).",
            },
            {
                id: 'mcp-storage-data',
                title: 'Does Unified MCP store or log customer data?',
                answer: 'No. Unified MCP does not store end-customer data. All MCP tool calls are executed in real time against the source system and are not persisted or cached.',
            },
            {
                id: 'mcp-multi-cutomer-data',
                title: 'Can MCP tools access data across customers or connections?',
                answer: 'No. Each MCP request is scoped to a single customer connection. There is no shared context or cross-tenant access between connections.',
            },
            {
                id: 'mcp-prevent-scopes',
                title: 'How do you prevent over-scoped MCP tool access?',
                answer: "Unified MCP supports least-privilege access by default. Tools are exposed only if permitted by the connection's granted scopes and can be further restricted by explicit tool allowlists.",
            },
            {
                id: 'mcp-pii-removal',
                title: 'How do you remove PII/sensitive data from MCP tool results?',
                answer: 'Unified MCP supports the hide_sensitive parameter to remove PII/sensitive data from results. This can be configured at the connection level.',
            },
        ];

        const faqJsonLd = {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.title,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: faq.answer,
                },
            })),
        };

        useSEO({
            title: 'Security',
            description:
                'Unified safeguards customer data by design: no data storage at rest, ever. SOC 2 Type II, GDPR, CCPA, HIPAA, and PIPEDA certifications, plus segregated multi-region hosting.',
            jsonLd: faqJsonLd,
        });
    },
    data() {
        return {
            links: [
                {
                    label: "Privacy Policy",
                    url: "https://unified.to/privacy"
                },
                {
                    label: "GDPR",
                    url: "https://unified.to/gdpr"
                },
                {
                    label: "Terms of Service",
                    url: "https://unified.to/tos"
                },
                {
                    label: "Uptime Status",
                    url: "https://status.unified.to/"
                },
                {
                    label: "Trust Center",
                    url: "https://app.mycroft.io/trust/unified-to"
                },
            ],
            faqs: [
                {
                    id: 'single-tenant',
                    title: 'Does Unified offer a one-prem or single-tenant cloud hosting?',
                    answer: `Yes. Contact us to discuss our Enterprise plan which has options for on-prem or single-tenant cloud hosting.`,
                },
                {
                    id: 'linked-account-removal',
                    title: 'What happens to data when a linked account is removed?',
                    answer: `All associated configuration data is deleted immediately. Because Unified never stores end-customer data, there is nothing else to persist or erase.`,
                },
                {
                    id: 'logs-storage',
                    title: 'Where are logs stored and who controls them?',
                    answer: 'End-customer data is never written to Unified\'s logs. Operational metadata is minimized and redacted. There is an option to stream detailed logs directly to your own Datadog, Grafana, or ClickHouse.',
                },
                {
                    id: 'mcp-sensitive-data',
                    title: 'How does Unified MCP handle sensitive data in LLM API workflows?',
                    answer: 'MCP makes only scoped, authorized end-customer data available to your LLM tools. There is an option to remove PII sensitive data. There are additional restrictive configuration options to limit access.'
                },
                {
                    id: 'data-residency',
                    title: 'Where is Unified hosted, and how is data residency handled?',
                    answer: 'Unified runs on AWS, leveraging enterprise-grade physical and network security. Customers can select any AWS region for credential and secrets storage, with multi-tenant options in the US, EU, and APAC. For enterprise deployments, we also offer single-tenant environments with isolated servers and databases on request.',
                },
                {
                    id: 'sso-saml',
                    title: 'Do you support SSO / SAML?',
                    answer: 'Yes. Unified supports SAML 2.0 and OIDC SSO. Connect your IdP (e.g., Okta, Microsoft Entra ID/Azure AD, Google Workspace, Ping, OneLogin) to enforce org policies like MFA and conditional access. Roles (admin/member) are applied in Unified, with optional IP allowlisting and per-developer/per-environment API tokens.'
                },
                {
                    id: 'rbac',
                    title: 'How does RBAC work?',
                    answer: 'Unified supports admin and member roles. Since we don\'t use email/password logins, access enforcement flows through your chosen IdP.',
                },
                {
                    id: 'ip-restrictions',
                    title: 'Can we restrict by IP?',
                    answer: 'Yes. Unified supports IP whitelisting, so only your servers can interact with Unified.'
                },
                {
                    id: 'security-testing',
                    title: 'How do you test for security issues?',
                    answer: 'Security is embedded in our SDLC. Every build is scanned with SAST and regular automated vulnerability scans run on containers and infrastructure.',
                },
                {
                    id: 'soc2',
                    title: 'Is Unified.to SOC 2 compliant?',
                    answer: `
Unified.to has successfully completed the SOC 2 Type 2 audit and is now fully compliant to AICPA's Trust Services Criteria 2017 (SOC 2).

For more details, please visit our Trust Center or <a class='underline' href='https://unified.to/contact'>contact us</a>.`,
                },
                {
                    id: 'mcp-sensitive-data',
                    title: 'How does Unified MCP handle sensitive data in LLM API workflows?',
                    answer: 'Unified MCP exposes only scoped, authorized end-customer data to LLM tools. Sensitive fields can be removed entirely using infrastructure-level controls before data reaches the model. Access can be further restricted by permissions, tool allowlists, and connection scope.',
                },
                {
                    id: 'mcp-storage-data',
                    title: 'Does Unified MCP store or log customer data?',
                    answer: 'No. Unified MCP does not store end-customer data. All MCP tool calls are executed in real time against the source system and are not persisted or cached. End-customer data is not written to logs; only minimal operational metadata is retained.'
                },
                {
                    id: 'mcp-multi-cutomer-data',
                    title: 'Can MCP tools access data across customers or connections?',
                    answer: 'No. Each MCP request is scoped to a single customer connection. There is no shared context or cross-tenant access between connections. MCP tools operate only within the permissions and boundary of the specified connection.'
                },
                {
                    id: 'mcp-prevent-scopes',
                    title: 'How do you prevent over-scoped MCP tool access?',
                    answer: 'Unified MCP supports least-privilege access by default. Tools are exposed only if permitted by the connection’s granted scopes and can be further restricted by explicit tool allowlists. Models never see tools they are not allowed to use.'
                },


            ] as Array<{ title: string; answer: string; id?: string }>,
            accordion_method: 0,
        }
    },
    methods: {
        getVSlot(index: number) {
            return `section-${index + 1}`
        },
    }
}
</script>